import type { ReactNode } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";

type FloatingToastProps = {
  visible: boolean;
  toastKey: string;
  accentColor: string;
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  positionStrategy?: "fixed" | "absolute";
  placement?: "top-center" | "bottom-end";
  top?: string;
  zIndex?: number;
  onClick?: () => void;
  onDismiss?: () => void;
  progressDurationSeconds?: number;
  progressKey?: string;
};

export default function FloatingToast({
  visible,
  toastKey,
  accentColor,
  icon,
  title,
  description,
  badge,
  positionStrategy = "fixed",
  placement = "top-center",
  top = "1rem",
  zIndex = 9997,
  onClick,
  onDismiss,
  progressDurationSeconds,
  progressKey,
}: FloatingToastProps) {
  const isBottomEnd = placement === "bottom-end";
  const toast = (
    <AnimatePresence>
      {visible && (
        <div
          style={{
            position: positionStrategy,
            ...(isBottomEnd
              ? { right: "1rem", bottom: "1rem" }
              : { top, left: "50%", transform: "translateX(-50%)" }),
            zIndex,
            width: "calc(100% - 2rem)",
            maxWidth: "26rem",
            pointerEvents: "none",
          }}
        >
          <motion.div
            key={toastKey}
            initial={{ opacity: 0, y: isBottomEnd ? 72 : -72 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: isBottomEnd ? 56 : -56,
              transition: { duration: 0.18, ease: "easeIn" },
            }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            style={{ pointerEvents: "auto" }}
          >
            <Box
              bg="rgba(255,255,255,0.88)"
              style={{ backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
              rounded="0.85rem"
              boxShadow="0 4px 24px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)"
              overflow="hidden"
              border={`1px solid ${accentColor}2e`}
              cursor={onClick ? "pointer" : "default"}
              onClick={onClick}
            >
              <Flex align="center" gap="0.75rem" px="0.85rem" py="0.75rem">
                <Flex
                  w="2.2rem"
                  h="2.2rem"
                  rounded="0.5rem"
                  bg={accentColor}
                  color="white"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  {icon}
                </Flex>

                <Box flex="1" minW={0}>
                  <Flex align="center" gap="0.4rem" flexWrap="wrap">
                    <Text fontSize="0.83rem" fontWeight="700" color="#1e293b" lineHeight="1.3">
                      {title}
                    </Text>
                    {badge}
                  </Flex>
                  {description && (
                    <Box fontSize="0.72rem" color="#64748b" lineHeight="1.4" mt="0.06rem">
                      {description}
                    </Box>
                  )}
                </Box>

                {onDismiss && (
                  <Box
                    w="1.5rem"
                    h="1.5rem"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="#94a3b8"
                    cursor="pointer"
                    rounded="full"
                    flexShrink={0}
                    _hover={{ color: "#475569", bg: "#f1f5f9" }}
                    transition="all 0.15s"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss();
                    }}
                  >
                    <X size={13} />
                  </Box>
                )}
              </Flex>

              {progressDurationSeconds && (
                <Box h="3px" bg="#e2e8f0" position="relative" overflow="hidden">
                  <motion.div
                    key={progressKey}
                    initial={{ scaleX: 1 }}
                    animate={{ scaleX: 0 }}
                    transition={{ duration: progressDurationSeconds, ease: "linear" }}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      height: "100%",
                      width: "100%",
                      background: accentColor,
                      transformOrigin: "right",
                    }}
                  />
                </Box>
              )}
            </Box>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (positionStrategy === "fixed" && typeof document !== "undefined") {
    return createPortal(toast, document.body);
  }

  return toast;
}
